import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail, User, ArrowLeft } from "lucide-react";
import MarketingHeader from "../../components/MarketingHeader";
import CountrySelect from "../../components/CountrySelect";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { findByIso } from "../../data/countries";
import { getPlan, getPayroll } from "../../data/plans";
import { register } from "../../services/api";
import { useTranslation } from "react-i18next";
import { inputBase, inputStyle } from './utils';
import { Stepper } from './components/Stepper';
import { Field } from './components/Field';
import { OrderSummary } from './components/OrderSummary';

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("plan") || "essentials";
  const billing = params.get("billing") || "monthly";
  const payrollId = params.get("payroll") || null;

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (token) {
      const next = new URLSearchParams();
      if (planId) next.set("plan", planId);
      if (billing) next.set("billing", billing);
      if (payrollId) next.set("payroll", payrollId);
      navigate("/billing?" + next.toString(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plan = getPlan(planId);
  const payroll = getPayroll(payrollId);

  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [iso, setIso] = useState<string>("CA");
  const [phone, setPhone] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirm, setConfirm] = useState<string>("");
  const [showPw, setShowPw] = useState<boolean>(false);
  const [showCfm, setShowCfm] = useState<boolean>(false);
  const [marketing, setMarketing] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (typeof document === "undefined" || document.getElementById("novala-fonts")) return;
    const l = document.createElement("link");
    l.id = "novala-fonts"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(l);
  }, []);

  const total = plan.monthlyPrice + (payroll ? payroll.monthlyPrice : 0);
  const totalOriginal = (plan.originalPrice || plan.monthlyPrice) + (payroll ? (payroll.originalPrice || payroll.monthlyPrice) : 0);
  const savings = totalOriginal - total;

  const submit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");
    if (!fullName.trim()) { setError(t("checkout.errors.fullNameRequired")); return; }
    if (!email.includes("@")) { setError(t("checkout.errors.emailInvalid")); return; }
    if (!phone.trim()) { setError(t("checkout.errors.phoneRequired")); return; }
    if (password.length < 8) { setError(t("checkout.errors.passwordTooShort")); return; }
    if (password !== confirm) { setError(t("checkout.errors.passwordMismatch")); return; }

    setSubmitting(true);
    try {
      const country = findByIso(iso);
      const parsed = parsePhoneNumberFromString(phone.trim(), iso as any);
      if (!parsed || !parsed.isValid()) {
        setError(t("checkout.errors.phoneInvalid", { country: country.name }));
        setSubmitting(false);
        return;
      }
      const fullPhone = parsed.format("E.164");
      const res = await register(email.trim(), password, fullName.trim(), "");
      const token = (res.data as { access_token: string }).access_token;
      localStorage.setItem("token", token);
      localStorage.setItem("user_email", email.trim());
      localStorage.setItem("user_name", fullName.trim());
      const first = fullName.trim().split(/\s+/)[0] || "";
      if (first) {
        const firstName = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
        localStorage.setItem("first_name", firstName);
        localStorage.setItem("full_name", fullName.trim());
      }
      localStorage.setItem("saved_account_email", email.trim());
      localStorage.setItem("signup_phone", fullPhone);
      localStorage.setItem("signup_country_iso", iso);
      localStorage.setItem("signup_marketing_opt_in", marketing ? "1" : "0");
      const qs = new URLSearchParams({ fromCheckout: "true", plan: planId, billing });
      if (payrollId) qs.set("payroll", payrollId);
      navigate("/verify-code?" + qs.toString());
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        if (detail.toLowerCase().includes("exist") || detail.toLowerCase().includes("already")) {
          setError(t("checkout.errors.accountExists"));
        } else { setError(detail); }
      } else { setError(t("checkout.errors.generic")); }
    } finally { setSubmitting(false); }
  };

  const inputCommon = inputStyle();
  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => { e.target.style.borderColor = "#046A38"; e.target.style.boxShadow = "0 0 0 3px rgba(4,106,56,0.12)"; },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => { e.target.style.borderColor = "#E2E8E8"; e.target.style.boxShadow = "none"; },
  };

  return (
    <div className="min-h-screen bg-white text-ink font-sans" style={{ background: "#F9FAFA" }}>
      <MarketingHeader />
      <div className="mx-auto" style={{ maxWidth: 1240, padding: "32px 32px 96px" }}>
        <button
          onClick={() => navigate(`/cart?plan=${planId}&billing=${billing}` + (payrollId ? `&payroll=${payrollId}` : ""))}
          className="mb-4 inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-stroke bg-white px-4 py-2.5 text-small font-semibold text-sub font-sans"
          style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
        >
          <ArrowLeft size={14} /> {t("checkout.backToCart")}
        </button>

        <Stepper active={3} />

        <div className="mb-8 text-center">
          <h1 className="m-0 font-extrabold text-ink" style={{ fontSize: "clamp(26px, 3.5vw, 36px)", letterSpacing: "-0.025em" }}>{t("checkout.title")}</h1>
          <p className="mt-2 text-body text-sub">{t("checkout.subtitle")}</p>
        </div>

        <div className="grid items-start gap-8" style={{ gridTemplateColumns: "1.4fr 1fr" }}>

          <form onSubmit={submit} className="rounded-2xl border border-hairline bg-white p-8">

            {error && (
              <div className="mb-[18px] rounded-[10px] p-3 text-small" style={{ background: "rgba(217,69,60,0.08)", border: "1px solid rgba(217,69,60,0.25)", color: "#D9453C" }}>
                {error}
              </div>
            )}

            <Field label={t("checkout.fields.fullName")}>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t("checkout.fields.fullNamePlaceholder")}
                  className={inputCommon ? inputBase : inputBase}
                  style={{ ...inputCommon, paddingLeft: 38 }}
                  {...focusHandlers} />
              </div>
            </Field>

            <Field label={t("checkout.fields.email")}>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t("checkout.fields.emailPlaceholder")}
                  className={inputBase}
                  style={{ ...inputCommon, paddingLeft: 38 }}
                  {...focusHandlers} />
              </div>
            </Field>

            <Field label={t("checkout.fields.phone")}>
              <div className="flex gap-2">
                <div className="shrink-0" style={{ width: 170 }}>
                  <CountrySelect mode="phone" value={iso} onChange={(c: { code: string }) => setIso(c.code)} defaultCode="CA" placeholder="Country" ariaLabel="Country code" />
                </div>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value.replace(/[^\d\s\-]/g, ""))} placeholder={t("checkout.fields.phonePlaceholder")}
                  className={inputBase + " flex-1"}
                  style={inputCommon}
                  {...focusHandlers} />
              </div>
            </Field>

            <Field label={t("checkout.fields.password")}>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder={t("checkout.fields.passwordPlaceholder")}
                  className={inputBase}
                  style={{ ...inputCommon, paddingLeft: 38, paddingRight: 42 }}
                  {...focusHandlers} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 border-none bg-transparent p-2 text-muted cursor-pointer" aria-label="Toggle password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>

            <Field label={t("checkout.fields.confirmPassword")}>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input type={showCfm ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t("checkout.fields.confirmPasswordPlaceholder")}
                  className={inputBase}
                  style={{ ...inputCommon, paddingLeft: 38, paddingRight: 42 }}
                  {...focusHandlers} />
                <button type="button" onClick={() => setShowCfm(!showCfm)} className="absolute right-2 top-1/2 -translate-y-1/2 border-none bg-transparent p-2 text-muted cursor-pointer" aria-label="Toggle confirm">
                  {showCfm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>

            <label className="mb-6 mt-2 flex cursor-pointer items-start gap-2.5">
              <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)}
                className="mt-0.5 cursor-pointer"
                style={{ width: 16, height: 16, accentColor: "#046A38" }} />
              <span className="text-small text-sub leading-normal">
                {t("checkout.marketingOptIn")}
              </span>
            </label>

            <button type="submit" disabled={submitting}
              onMouseEnter={e => { if (!submitting) { e.currentTarget.style.background = "#0F6E56"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
              onMouseLeave={e => { if (!submitting) { e.currentTarget.style.background = "linear-gradient(180deg, #0F6E56 0%, #046A38 100%)"; e.currentTarget.style.transform = "none"; } }}
              className={"w-full inline-flex items-center justify-center gap-2 rounded-2xl text-body font-extrabold text-white font-sans transition-all " + (submitting ? "cursor-wait bg-muted border-none" : "cursor-pointer")}
              style={submitting ? { padding: "15px 20px", letterSpacing: "0.01em" } : { padding: "15px 20px", letterSpacing: "0.01em", background: "linear-gradient(180deg, #0F6E56 0%, #046A38 100%)", border: "1px solid #033D22", boxShadow: "0 10px 24px -6px rgba(4,106,56,0.45), 0 2px 4px rgba(0,0,0,0.06)" }}>
              {submitting ? t("checkout.submittingButton") : <>{t("checkout.submitButton")} <ArrowRight size={16} strokeWidth={2.4} /></>}
            </button>

            <p className="mt-4 text-center text-tiny text-muted leading-relaxed">
              {t("checkout.legalNote")}
            </p>

            <p className="mt-3 text-center text-sub" style={{ fontSize: 13.5 }}>
              {t("checkout.alreadyHaveAccount")} <button type="button" onClick={() => navigate("/login")} className="border-none bg-transparent p-0 font-semibold text-brand cursor-pointer font-sans" style={{ fontSize: 13.5 }}>{t("checkout.signIn")}</button>
            </p>
          </form>

          <OrderSummary plan={plan} payroll={payroll} planId={planId} payrollId={payrollId} total={total} savings={savings} t={t} language={i18n.language} />
        </div>
      </div>
    </div>
  );
}
